import { SeoHead } from '@/components/SeoHead';
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useQuoteDrawer } from "@/contexts/QuoteDrawerContext";
import { Figma, Users, Zap, Eye, Award, Layout, Palette, Smartphone, ChevronDown, MessageSquare, Check, Lightbulb, Layers, Target, Code, Smartphone as Mobile, PenTool } from "lucide-react";
import { useState } from "react";

const FAQ = ({ question, answer }: { question: string; answer: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
      <button
        className="flex justify-between items-center w-full text-left py-3 focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-medium text-lg">{question}</span>
        <ChevronDown className={`h-5 w-5 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="mt-2 text-muted-foreground">
          {answer}
        </div>
      )}
    </div>
  );
};

export default function UIUX() {
  const { openDrawer } = useQuoteDrawer();

  const services = [
    {
      title: "User Research & Analysis",
      description: "Deep understanding of your users through research and data analysis.",
      icon: <Users className="h-8 w-8" />
    },
    {
      title: "UI/UX Design",
      description: "Beautiful and intuitive interfaces that enhance user experience.",
      icon: <Figma className="h-8 w-8" />
    },
    {
      title: "Prototyping & Testing",
      description: "Interactive prototypes and usability testing for optimal user flows.",
      icon: <Zap className="h-8 w-8" />
    },
    {
      title: "Design Systems",
      description: "Consistent design systems and component libraries for scalability.",
      icon: <Eye className="h-8 w-8" />
    }
  ];

  const whyChooseUs = [
    {
      icon: <Award className="h-8 w-8" />,
      title: "Award-Winning Designers",
      description: "Our team has been recognized for excellence in UI/UX design"
    },
    {
      icon: <Layout className="h-8 w-8" />,
      title: "User-First Approach",
      description: "We prioritize user needs and behaviors in every design decision"
    },
    {
      icon: <Palette className="h-8 w-8" />,
      title: "Pixel-Perfect Design",
      description: "Meticulous attention to detail in every pixel we create"
    },
    {
      icon: <Smartphone className="h-8 w-8" />,
      title: "Responsive & Adaptive",
      description: "Designs that work seamlessly across all devices and screen sizes"
    }
  ];

  const designPrinciples = [
    {
      title: "Simplicity",
      description: "Clean, intuitive interfaces that reduce cognitive load",
      icon: <Check className="h-6 w-6 text-accent" />
    },
    {
      title: "Consistency",
      description: "Uniform design patterns for predictable user experiences",
      icon: <Layers className="h-6 w-6 text-accent" />
    },
    {
      title: "Accessibility",
      description: "Inclusive design that works for all users",
      icon: <Target className="h-6 w-6 text-accent" />
    },
    {
      title: "Innovation",
      description: "Creative solutions that push boundaries",
      icon: <Lightbulb className="h-6 w-6 text-accent" />
    }
  ];

  const portfolioItems = [
    {
      title: "E-commerce Platform Redesign",
      category: "Web Design",
      description: "Increased conversions by 45% with intuitive navigation",
      image: "/images/portfolio/ecommerce-redesign.jpg"
    },
    {
      title: "Mobile Banking App",
      category: "Mobile App",
      description: "Simplified banking experience for a major financial institution",
      image: "/images/portfolio/banking-app.jpg"
    },
    {
      title: "SaaS Dashboard",
      category: "Web App",
      description: "Data visualization and analytics dashboard for B2B SaaS",
      image: "/images/portfolio/saas-dashboard.jpg"
    }
  ];

  const testimonials = [
    {
      quote: "The UI/UX work transformed our digital product. User engagement increased by 60% and support tickets decreased by 45%.",
      author: "Sarah Al Maktoum",
      role: "Product Manager, TechInnovate"
    },
    {
      quote: "Their attention to detail and user-centered approach resulted in a mobile app that our customers love to use.",
      author: "Omar Al Hashimi",
      role: "CEO, FinTech Solutions"
    }
  ];

  const faqs = [
    {
      question: "What's the difference between UI and UX design?",
      answer: "UI (User Interface) focuses on the visual elements and layout, while UX (User Experience) encompasses the overall feel and usability of the product."
    },
    {
      question: "How long does a typical UI/UX project take?",
      answer: "Timelines vary based on project scope, but most UI/UX projects take between 4-12 weeks from research to final deliverables."
    },
    {
      question: "Do you conduct user testing?",
      answer: "Yes, we incorporate user testing at multiple stages to validate design decisions and ensure optimal user experience."
    },
    {
      question: "What design tools do you use?",
      answer: "We primarily use Figma, but are also proficient in Sketch, Adobe XD, and other industry-standard design tools."
    }
  ];

  return (
    <>
      <SeoHead 
        title="UI/UX Design Services UAE | ZeeX Soft"
        description="Professional UI/UX design in Dubai & Sharjah. Create intuitive digital experiences that convert visitors into customers."
        keywords="ui ux design uae, web design dubai, mobile app ui sharjah"
      />

      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-gray-900 to-gray-800 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          {/* Animated design elements */}
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            {[...Array(20)].map((_, i) => (
              <div 
                key={i}
                className="absolute rounded-full bg-pink-500"
                style={{
                  width: `${Math.random() * 10 + 5}px`,
                  height: `${Math.random() * 10 + 5}px`,
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animation: `pulse ${Math.random() * 5 + 3}s infinite alternate`
                }}
              />
            ))}
          </div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center lg:text-left"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-pink-400 to-purple-500">
                  UI/UX Design
                </span>
              </h1>
              <p className="text-xl text-gray-300 mb-8 max-w-lg">
                Beautiful, intuitive interfaces designed to delight users and drive engagement.
              </p>
              
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start mb-12">
                {['Wireframing', 'Prototyping', 'User Testing', 'Design Systems'].map((service) => (
                  <span key={service} className="px-4 py-2 bg-gray-800/50 backdrop-blur-sm rounded-full text-gray-200 border border-gray-700">
                    {service}
                  </span>
                ))}
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-3 bg-gradient-to-r from-pink-500 to-purple-600 rounded-lg font-medium text-white shadow-lg"
                onClick={() => openDrawer('UI/UX Design')}
              >
                Get Design Audit
              </motion.button>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              {/* Glass morphism card */}
              <div className="relative bg-gray-800/50 backdrop-blur-md border border-gray-700/50 rounded-2xl p-8 shadow-2xl overflow-hidden">
                <div className="absolute -right-20 -top-20 w-64 h-64 bg-purple-500/5 rounded-full filter blur-xl"></div>
                <div className="flex justify-center mb-8">
                  <PenTool className="h-20 w-20 text-pink-400" />
                </div>
                
                {/* UI mockup */}
                <div className="relative h-48 bg-gray-800 rounded-lg border border-gray-700 mb-6 overflow-hidden">
                  {/* Device frame */}
                  <div className="absolute inset-0 border-8 border-gray-700 rounded-xl"></div>
                  
                  {/* App screen */}
                  <div className="absolute inset-1 bg-gray-900 rounded">
                    {/* App header */}
                    <div className="h-8 bg-gray-800 flex items-center px-3">
                      <div className="flex space-x-2">
                        <div className="h-2 w-2 rounded-full bg-red-500"></div>
                        <div className="h-2 w-2 rounded-full bg-yellow-500"></div>
                        <div className="h-2 w-2 rounded-full bg-green-500"></div>
                      </div>
                    </div>
                    
                    {/* App content */}
                    <div className="p-3">
                      <div className="h-4 w-24 bg-gray-700 rounded mb-2"></div>
                      <div className="grid grid-cols-3 gap-2 mb-2">
                        {[...Array(6)].map((_, i) => (
                          <div key={i} className="h-8 bg-gray-800 rounded"></div>
                        ))}
                      </div>
                      <div className="h-3 w-32 bg-gray-700 rounded"></div>
                    </div>
                  </div>
                </div>
                
                {/* Design principles */}
                <div className="grid grid-cols-2 gap-3">
                  {['Accessibility', 'Consistency', 'Feedback', 'Hierarchy'].map((principle) => (
                    <div key={principle} className="p-2 bg-gray-800 rounded-lg border border-gray-700 flex items-center">
                      <div className="h-6 w-6 bg-pink-500/20 rounded-lg mr-2 flex items-center justify-center">
                        <div className="h-2 w-2 bg-pink-400 rounded-full"></div>
                      </div>
                      <div className="text-sm text-gray-300">{principle}</div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Floating design badges */}
              <div className="absolute -bottom-5 -right-5 flex space-x-2">
                {['Figma', 'Adobe XD', 'Sketch', 'Framer'].map((tool) => (
                  <span key={tool} className="px-2 py-1 text-xs font-medium rounded-full bg-gray-800/80 text-gray-300 backdrop-blur-sm">
                    {tool}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">UI/UX Design Services</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              User-centered design approach for digital products that users love
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-effect p-8 rounded-xl"
              >
                <div className="w-16 h-16 bg-accent/20 rounded-lg flex items-center justify-center mb-6 text-accent">
                  {service.icon}
                </div>
                <h3 className="font-orbitron font-semibold text-xl mb-4">{service.title}</h3>
                <p className="text-muted-foreground">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Design Process */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Design Process</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Human-centered design methodology for optimal user experiences
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {[
              { step: "01", title: "Research", description: "User research and market analysis" },
              { step: "02", title: "Define", description: "Problem definition and user personas" },
              { step: "03", title: "Ideate", description: "Creative solutions and concepts" },
              { step: "04", title: "Prototype", description: "Interactive prototypes and wireframes" },
              { step: "05", title: "Test", description: "Usability testing and refinement" }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-accent to-primary rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="font-orbitron font-bold text-primary-foreground">{item.step}</span>
                </div>
                <h3 className="font-orbitron font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Why Choose Our Design Services?</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We create meaningful digital experiences that users love
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-effect p-8 rounded-xl text-center hover:shadow-lg transition-shadow"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white">
                  {item.icon}
                </div>
                <h3 className="font-orbitron font-semibold text-xl mb-4">{item.title}</h3>
                <p className="text-muted-foreground">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Design Principles */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Our Design Principles</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Guiding principles that shape every design decision we make
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {designPrinciples.map((principle, index) => (
              <motion.div
                key={principle.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-effect p-8 rounded-xl hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mx-auto mb-6">
                  {principle.icon}
                </div>
                <h3 className="font-orbitron font-semibold text-xl mb-3">{principle.title}</h3>
                <p className="text-muted-foreground">{principle.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Design Work */}
      <section className="py-20 bg-gray-900/50">
        <div className="container mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-pink-400 to-purple-500">
                Our Design Work
              </span>
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Explore our portfolio of beautifully crafted interfaces
            </p>
          </motion.div>

          {/* Filter tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {['All', 'Web', 'Mobile', 'SaaS', 'E-commerce'].map((filter) => (
              <button
                key={filter}
                className="px-4 py-2 rounded-full text-sm font-medium transition-all duration-300"
                style={{
                  backgroundColor: filter === 'All' ? 'rgba(192, 132, 252, 0.2)' : 'rgba(55, 65, 81, 0.5)',
                  color: filter === 'All' ? '#e9d5ff' : '#d1d5db',
                  border: filter === 'All' ? '1px solid rgba(192, 132, 252, 0.3)' : '1px solid rgba(55, 65, 81, 0.5)'
                }}
              >
                {filter}
              </button>
            ))}
          </div>

          {/* Portfolio grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'E-commerce Platform',
                description: 'Modern shopping experience with intuitive navigation',
                type: 'Web',
                tech: ['React', 'Figma', 'Tailwind']
              },
              {
                title: 'Fitness Mobile App',
                description: 'Personalized workout tracking with clean UI',
                type: 'Mobile',
                tech: ['React Native', 'Framer']
              },
              {
                title: 'SaaS Dashboard',
                description: 'Data visualization with powerful analytics',
                type: 'SaaS',
                tech: ['Vue', 'D3.js']
              },
              {
                title: 'Travel Booking',
                description: 'Seamless booking flow with interactive maps',
                type: 'Web',
                tech: ['Next.js', 'Mapbox']
              },
              {
                title: 'Banking App',
                description: 'Secure financial management interface',
                type: 'Mobile',
                tech: ['Flutter', 'Firebase']
              },
              {
                title: 'CMS Interface',
                description: 'Content management with drag-and-drop',
                type: 'Web',
                tech: ['React', 'GraphQL']
              }
            ].map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="group relative overflow-hidden rounded-xl bg-gray-800/50 border border-gray-700/50 backdrop-blur-sm"
              >
                {/* Project thumbnail */}
                <div className="h-48 bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
                  {project.type === 'Web' && (
                    <svg width="100%" height="100%" viewBox="0 0 300 180" className="rounded-lg">
                      <rect width="100%" height="100%" fill="#1F2937" rx="8" ry="8"/>
                      <rect x="10" y="10" width="280" height="30" rx="4" fill="#374151"/>
                      <rect x="20" y="20" width="40" height="10" rx="2" fill="#6B7280"/>
                      <rect x="240" y="20" width="40" height="10" rx="2" fill="#6B7280"/>
                      <rect x="10" y="50" width="100" height="10" rx="2" fill="#4B5563"/>
                      <rect x="10" y="70" width="280" height="60" rx="4" fill="#374151"/>
                      <rect x="20" y="80" width="60" height="40" rx="2" fill="#7C3AED"/>
                      <rect x="90" y="80" width="60" height="40" rx="2" fill="#EC4899"/>
                      <rect x="160" y="80" width="60" height="40" rx="2" fill="#3B82F6"/>
                      <rect x="230" y="80" width="60" height="40" rx="2" fill="#10B981"/>
                    </svg>
                  )}
                  
                  {project.type === 'Mobile' && (
                    <svg width="120" height="200" viewBox="0 0 120 200" className="rounded-xl border-4 border-gray-600">
                      <rect width="120" height="200" fill="#111827" rx="12" ry="12"/>
                      <rect x="10" y="10" width="100" height="180" fill="#1F2937" rx="4" ry="4"/>
                      <rect x="20" y="20" width="80" height="20" rx="2" fill="#4B5563"/>
                      <circle cx="60" cy="190" r="4" fill="#374151"/>
                      <rect x="20" y="50" width="80" height="120" rx="4" fill="#374151"/>
                      <rect x="30" y="60" width="60" height="20" rx="2" fill="#7C3AED"/>
                      <rect x="30" y="90" width="60" height="10" rx="2" fill="#6B7280"/>
                      <rect x="30" y="110" width="60" height="10" rx="2" fill="#6B7280"/>
                      <rect x="30" y="130" width="60" height="40" rx="4" fill="#EC4899"/>
                    </svg>
                  )}
                  
                  {project.type === 'SaaS' && (
                    <svg width="100%" height="100%" viewBox="0 0 300 180" className="rounded-lg">
                      <rect width="100%" height="100%" fill="#1F2937" rx="8" ry="8"/>
                      <rect x="10" y="10" width="280" height="30" rx="4" fill="#374151"/>
                      <rect x="20" y="20" width="40" height="10" rx="2" fill="#6B7280"/>
                      <rect x="240" y="20" width="40" height="10" rx="2" fill="#6B7280"/>
                      <rect x="10" y="50" width="280" height="120" rx="4" fill="#374151"/>
                      <rect x="20" y="60" width="260" height="20" rx="2" fill="#3B82F6"/>
                      <rect x="20" y="90" width="100" height="70" rx="4" fill="#10B981"/>
                      <rect x="130" y="90" width="150" height="30" rx="4" fill="#EC4899"/>
                      <rect x="130" y="130" width="70" height="30" rx="4" fill="#F59E0B"/>
                      <rect x="210" y="130" width="70" height="30" rx="4" fill="#7C3AED"/>
                    </svg>
                  )}
                  
                  {project.type === 'E-commerce' && (
                    <svg width="100%" height="100%" viewBox="0 0 300 180" className="rounded-lg">
                      <rect width="100%" height="100%" fill="#1F2937" rx="8" ry="8"/>
                      <rect x="10" y="10" width="280" height="30" rx="4" fill="#374151"/>
                      <rect x="20" y="20" width="40" height="10" rx="2" fill="#6B7280"/>
                      <rect x="240" y="20" width="40" height="10" rx="2" fill="#6B7280"/>
                      <rect x="10" y="50" width="100" height="10" rx="2" fill="#4B5563"/>
                      <rect x="10" y="70" width="280" height="100" rx="4" fill="#374151"/>
                      <rect x="20" y="80" width="80" height="80" rx="4" fill="#F59E0B"/>
                      <rect x="110" y="80" width="80" height="80" rx="4" fill="#EC4899"/>
                      <rect x="200" y="80" width="80" height="80" rx="4" fill="#10B981"/>
                      <rect x="20" y="170" width="260" height="5" rx="2" fill="#6B7280"/>
                    </svg>
                  )}
                </div>
                
                {/* Project info */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-2">{project.title}</h3>
                  <p className="text-gray-300 mb-4">{project.description}</p>
                  
                  {/* Tech badges */}
                  <div className="flex flex-wrap gap-2">
                    {project.tech.map((tech) => (
                      <span 
                        key={tech} 
                        className="px-2 py-1 text-xs rounded-full bg-gray-800 text-gray-300"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
                
                {/* Hover overlay */}
                <Link href="/portfolio" className="absolute inset-0 bg-gradient-to-t from-pink-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                  <button className="w-full py-2 bg-pink-600 hover:bg-pink-700 rounded-lg text-white font-medium transition-colors">
                    View Case Study
                  </button>
                </Link>
              </motion.div>
            ))}
          </div>
          
          {/* View more button */}
          <div className="text-center mt-12">
            <Link href="/portfolio" className="inline-block">
              <button className="px-6 py-3 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-800/50 hover:border-gray-600 transition-colors duration-300">
                View Full Portfolio
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">What Our Clients Say</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Success stories from businesses we've helped transform
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="glass-effect p-8 rounded-xl"
              >
                <div className="flex items-center mb-6">
                  <div className="text-yellow-400 mr-2">
                    {[...Array(5)].map((_, i) => (
                      <span key={i}>★</span>
                    ))}
                  </div>
                </div>
                <p className="text-lg italic mb-6">"{testimonial.quote}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center text-white font-bold mr-4">
                    {testimonial.author.charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-semibold">{testimonial.author}</h4>
                    <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Technologies */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Our Design Stack</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Professional design tools we use to create exceptional user experiences
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {[
              { name: "Figma", icon: "fas fa-pencil-ruler" },
              { name: "Sketch", icon: "fas fa-vector-square" },
              { name: "Adobe XD", icon: "fas fa-shapes" },
              { name: "InVision", icon: "fas fa-layer-group" },
              { name: "Framer", icon: "fas fa-cube" },
              { name: "Webflow", icon: "fas fa-code" },
              { name: "After Effects", icon: "fas fa-film" },
              { name: "Photoshop", icon: "fas fa-image" },
              { name: "Illustrator", icon: "fas fa-paint-brush" },
              { name: "Miro", icon: "fas fa-th" },
              { name: "Zeplin", icon: "fas fa-code-branch" },
              { name: "Principle", icon: "fas fa-play-circle" }
            ].map((tool, index) => (
              <motion.div
                key={tool.name}
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="glass-effect p-6 rounded-xl text-center hover:shadow-lg transition-shadow"
              >
                <i className={`${tool.icon} text-3xl text-accent mb-3`}></i>
                <p className="font-orbitron text-sm">{tool.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Frequently Asked Questions</h2>
            <p className="text-xl text-muted-foreground">
              Everything you need to know about our UI/UX design services
            </p>
          </motion.div>
          
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <FAQ key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-pink-500/10 to-purple-600/10 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('/svgs/pattern-grid.svg')] [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]"></div>
        </div>
        <div className="container mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="font-orbitron font-bold text-4xl md:text-5xl gradient-text mb-6">
              Ready to Transform Your Digital Experience?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Let's create intuitive, beautiful, and effective digital experiences that delight your users
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={openDrawer} 
                className="btn-primary px-8 py-6 text-lg font-semibold"
                size="lg"
              >
                Start Your Design Project
                <Figma className="ml-2 h-5 w-5" />
              </Button>
              <Link href="/contact">
                <Button 
                  className="btn-secondary px-8 py-6 text-lg font-semibold"
                  size="lg"
                >
                  Get Free Consultation
                  <MessageSquare className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              No obligation. No commitment. Just a conversation about your design needs.
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
}
